// contexts/AuthContext.js - Fixed for OTP Auto-Login
import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  // Login function - accepts either (email, password) OR (token, user)
  const login = (emailOrToken, passwordOrUser) => {
    try {
      // Case 1: Auto-login after OTP verification (token, user object)
      if (typeof passwordOrUser === 'object' && passwordOrUser !== null) {
        const authToken = emailOrToken;
        const userData = passwordOrUser;

        // Store in state
        setToken(authToken);
        setUser(userData);

        // Store in localStorage
        localStorage.setItem('token', authToken);
        localStorage.setItem('user', JSON.stringify(userData));

        console.log('✅ AuthContext: Auto-login successful', userData);
        return;
      }

      // Case 2: Manual login (email, password) - NOT USED in OTP flow
      // This is here for compatibility with old login flow
      if (typeof emailOrToken === 'string' && typeof passwordOrUser === 'string') {
        throw new Error('Manual email/password login should use authAPI.login() instead');
      }

      // Invalid arguments
      throw new Error('Invalid login parameters');
    } catch (error) {
      console.error('❌ AuthContext: Login failed', error);
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    console.log('✅ AuthContext: Logout successful');
  };

  // Update user data
  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;